import React, { memo, useState, useEffect, useRef } from 'react';
import { uuid4 } from '@sentry/utils';
import { Button } from '../../../aem-core-components';
import { useCookieValue } from '../../../aem-core-components/utils/hooks';
import Input from '../../global/atoms/input';
import RadioButton from '../../global/atoms/radioButton/RadioButton';
import AlertIcon from '../../../resources/images/alert-triangle-red.svg';
import { logGRError, getCreateAccountObject } from '../utils/guidedUtil';
import { grSectionShimmerEffect } from '../utils/shimmerUtil';
import { isValidString } from '../../global/utils/logger';
import useCreateAccount from './useCreateAccount';
import JoinAccount from '../joinAccountModal';
import { LoadingIndicator } from '../../../aem-core-components';
import { PERSONAL_ACCOUNT, BUSINESS_ACCOUNT, ACCOUNT_TYPE, EMAIL, USER_EXISTS, LOGIN_SCREEN } from '../constants';
import { EVENT_ECOMMERCE_NAMES_CONFIG } from '../../../constants/analyticsConstants/Ecommerce';
import { useAnalyticsContext } from '../../../config/GoogleTagManagerEvents';
import FreeEmailModal from '../freeEmailModal';
import { STORAGE_CONFIG } from '../../../constants/storageConfig';
import './createAccount.scss';

const CreateAccount = () => {
    const [showJoinAccountModal, setShowJoinAccountModal] = useState(false);
    const createAccountConfig = getCreateAccountObject();
    const {
        formState = {},
        handleInputChange = () => {},
        handleFocus = () => {},
        handleSubmit = () => {},
        onContinue = () => {},
        isLoading = false,
        showFreeEmailModal = false,
        setShowFreeEmailModal,
        handleFreeEmailContinue,
        saveFormDataState
    } = useCreateAccount();

    const {
        backButtonAriaLabel = '',
        businessAccountUseDisclaimer = '',
        businessAccountUseTitleLabel = '',
        continueButtonLabel = '',
        createAccountSubTitle = '',
        createAccountTitleLabel = '',
        emailAddressLabel = '',
        existingAccountLabel = '',
        joinAccountLabel = '',
        newAccountLabel = '',
        personalAccountUseDisclaimer = '',
        personalAccountUseTitleLabel = '',
        requiredFieldsLabel = '',
        signInLabel = ''
    } = createAccountConfig || {};

    const [sbsidCookie, setsbsidCookie] = useCookieValue('sbsid');
    const { sendEventsForGuidedRegistrationFormViewed, sendEventsForPageViewed } = useAnalyticsContext();
    const emailRef = useRef(null);

    let randomId = uuid4();
    if (!isValidString(sessionStorage.getItem('sbsid'))) {
        sessionStorage.setItem('sbsid', randomId);
        setsbsidCookie(randomId);
    }

    useEffect(() => {
        window.scrollTo(0, 0);
        grSectionShimmerEffect();
        readQueryParams();
        addReferer();
        sessionStorage.removeItem(STORAGE_CONFIG.SESSION_STORAGE.CCA_SUBMISSION_ID);
        sendEventsForGuidedRegistrationFormViewed(
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_FORM_VIEWED,
            EVENT_ECOMMERCE_NAMES_CONFIG.ECOMMERCE_GUIDED_REGISTRATION_FORM_NAME
        );
    }, []);

    const addReferer = () => {
        try {
            const referer = document.referrer;
            if (referer != '') {
                const refererPatterns = ['/cca', '/createaccount/personal', '/email-verify', '/convertProfile'];
                const shouldStoreReferer = !refererPatterns.some(pattern => referer.includes(pattern));
                if (shouldStoreReferer) {
                    sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.REFERER, referer);
                }
            }
        } catch (error) {
            logGRError(error);
        }
    };

    const readQueryParams = () => {
            const params = new URLSearchParams(window?.location?.search);
            const { c, s, o, salesrepid, sfaccountnumber, type, email, ers } = Object.fromEntries(params.entries());

        if (c || o || s || sfaccountnumber || salesrepid || type === 'business') {
            saveFormDataState(ACCOUNT_TYPE, BUSINESS_ACCOUNT);
        } 
        if (type === 'business' && email) {
            saveFormDataState(EMAIL, email.replace(/"/g, ''));
        }
        const salesrepId = s || salesrepid;
        if (salesrepId) {
            sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.SALESREPID, salesrepId);
        }

        if (sfaccountnumber) {
            sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.SFID, sfaccountnumber);
        }
        if (ers) {
            sessionStorage.setItem(STORAGE_CONFIG.SESSION_STORAGE.ERS, ers);
        }
    };

    const handleChangeEmail = () => {
        setShowFreeEmailModal(false);
        saveFormDataState(EMAIL, '');
        emailRef.current.focus();
    };

    const onJoinAccountClick = event => {
        event.preventDefault();
        setShowJoinAccountModal(true);
    };

    const getErrorMsg = errorMsg => {
        if (formState?.error?.[EMAIL]) {
            return formState?.error?.[EMAIL];
        } else if (formState?.error?.[USER_EXISTS]) {
            return (
                <>
                    {formState?.error?.[USER_EXISTS]}&nbsp;
                    <a
                        href={LOGIN_SCREEN}
                        data-testid="SignInButton"
                        className="error_input__span__a"
                        role="alert"
                        tabIndex="0">
                        Sign in
                    </a>
                </>
            );
        }
    };

    return (
        <div className={isLoading ? 'loading_overlay_background' : ''}>
            <section className="guidedForm__section">
                <div className="container">
                    <div className="row guidedForm__row">
                        <div className="col-lg-6">
                            <div className="form-heading guidedForm__heading">
                                <h2 className="guidedForm__title">{createAccountTitleLabel}</h2>
                                <small className="guidedForm__require-field-text">{requiredFieldsLabel}</small>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <fieldset className="guidedForm__fieldset">
                                    <legend
                                        className={`guidedForm__legend 
                                        ${formState?.error?.[ACCOUNT_TYPE] ? 'legend-error' : ''} requiredSymbol`}>
                                        {createAccountSubTitle}
                                    </legend>
                                    <div className="guidedForm__content">
                                        <div className="guidedForm__radio guidedForm__contents">
                                            <div role="radiogroup" className="d-flex guidedForm__radiogroup">
                                                <RadioButton
                                                    id="1"
                                                    name={ACCOUNT_TYPE}
                                                    value={PERSONAL_ACCOUNT}
                                                    checked={formState?.data?.[ACCOUNT_TYPE] === PERSONAL_ACCOUNT}
                                                    handleRadioChange={handleInputChange}
                                                    label={personalAccountUseTitleLabel}
                                                    customClassName={
                                                        formState?.error?.[ACCOUNT_TYPE] ? 'error_radio' : ''
                                                    }
                                                    dataTestId={`${ACCOUNT_TYPE}-${PERSONAL_ACCOUNT}`}
                                                />
                                            </div>
                                            <small className="guidedForm__radiobtn-heading">
                                                {personalAccountUseDisclaimer}
                                            </small>
                                        </div>
                                        <div className="guidedForm__radio guidedForm__contents">
                                            <div role="radiogroup" className="d-flex guidedForm__radiogroup">
                                                <RadioButton
                                                    id="2"
                                                    name={ACCOUNT_TYPE}
                                                    value={BUSINESS_ACCOUNT}
                                                    checked={formState?.data?.[ACCOUNT_TYPE] === BUSINESS_ACCOUNT}
                                                    handleRadioChange={handleInputChange}
                                                    label={businessAccountUseTitleLabel}
                                                    customClassName={
                                                        formState?.error?.[ACCOUNT_TYPE] ? 'error_radio' : ''
                                                    }
                                                    dataTestId={`${ACCOUNT_TYPE}-${BUSINESS_ACCOUNT}`}
                                                />
                                            </div>
                                            <small className="guidedForm__radiobtn-heading">
                                                {businessAccountUseDisclaimer}
                                            </small>
                                        </div>
                                        {formState?.error?.[ACCOUNT_TYPE] && (
                                            <div className="guidedForm__error_account">
                                                <AlertIcon />
                                                <span className="guidedForm__error_input">
                                                    {formState?.error?.[ACCOUNT_TYPE]}
                                                </span>
                                            </div>
                                        )}
                                        <div className="cmp-Field__field__root">
                                            <Input
                                                name={EMAIL}
                                                inputRef={emailRef}
                                                handleInputChange={handleInputChange}
                                                label={emailAddressLabel}
                                                inputAriaRequired={true}
                                                required={true}
                                                maxLength={50}
                                                value={formState?.data?.[EMAIL]}
                                                errorMsg={getErrorMsg()}
                                                showErrorIcon={true}
                                                onFocus={() => handleFocus(EMAIL)}
                                            />
                                        </div>
                                    </div>
                                </fieldset>
                                <Button
                                    data-testid="continue-button"
                                    className="button button-primary button-block"
                                    type="button"
                                    onClick={onContinue}
                                    buttonAriaLabel="Continue">
                                    {continueButtonLabel}
                                </Button>
                            </form>
                            <small className={`guidedForm__info_text`}>
                                {newAccountLabel}
                                <a href={LOGIN_SCREEN} className="guidedForm__link">
                                    {signInLabel}
                                </a>
                            </small>
                            <small className={`guidedForm__info_text guidedForm__text-border`}>
                                {existingAccountLabel}
                                <a href="#" className="guidedForm__link" onClick={onJoinAccountClick}>
                                    {joinAccountLabel}
                                </a>
                            </small>
                            {showJoinAccountModal && (
                                <JoinAccount
                                    showJoinAccountModal={showJoinAccountModal}
                                    setShowJoinAccountModal={setShowJoinAccountModal}
                                />
                            )}

                            {showFreeEmailModal && (
                                <FreeEmailModal
                                    showFreeEmailModal={showFreeEmailModal}
                                    setShowFreeEmailModal={setShowFreeEmailModal}
                                    handleFreeEmailContinue={handleFreeEmailContinue}
                                    changeEmailAddress={handleChangeEmail}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </section>
            {isLoading && (
                <div className="globalmodal__overlay loading_overlay">
                    <LoadingIndicator />
                </div>
            )}
        </div>
    );
};

export default memo(CreateAccount);
